"use client";

import { useTransition, useOptimistic, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  storeBookingIntent,
  clearBookingIntent,
  updateBookingIntentStep,
  getBookingCartData,
} from "~/server/actions/booking-cart-actions";
import {
  buildBookingReturnUrl,
  createBookingFormData,
  type BookingCartItem,
} from "~/lib/booking-cart";

/**
 * React hook for client components to interact with booking cart via Server Actions
 *
 * This hook provides a clean interface for managing booking cart state while
 * ensuring all cookie operations go through Server Actions (no document.cookie usage).
 * Now uses React Query for non-blocking data fetching.
 */

interface UseBookingCartReturn {
  cartItem: BookingCartItem | null;
  returnUrl: string | null;
  isPending: boolean;
  isLoading: boolean;
  addToBookingCart: (
    item: Omit<BookingCartItem, "createdAt" | "expiresAt">,
  ) => Promise<void>;
  clearBookingCart: () => Promise<void>;
  updateBookingStep: (step: number) => Promise<void>;
}

const BOOKING_CART_QUERY_KEY = ["booking-cart"] as const;

export function useBookingCart(): UseBookingCartReturn {
  const [isPending, startTransition] = useTransition();
  const queryClient = useQueryClient();

  // Fetch cart data with React Query
  const { data: cartData, isLoading } = useQuery({
    queryKey: BOOKING_CART_QUERY_KEY,
    queryFn: getBookingCartData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const cartItem = cartData?.cartItem || null;
  const returnUrl = cartData?.returnUrl || null;

  // Use optimistic updates for immediate UI feedback
  const [optimisticCartData, setOptimisticCartData] = useOptimistic<
    { cartItem: BookingCartItem | null; returnUrl: string | null },
    { cartItem: BookingCartItem | null; returnUrl: string | null }
  >({ cartItem, returnUrl }, (state, newState) => newState);

  const addToBookingCart = useCallback(
    async (item: Omit<BookingCartItem, "createdAt" | "expiresAt">) => {
      const optimisticItem: BookingCartItem = {
        ...item,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(
          Date.now() + 14 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      };
      const optimisticReturnUrl = buildBookingReturnUrl(
        optimisticItem,
        window.location.origin,
      );

      startTransition(() => {
        setOptimisticCartData({
          cartItem: optimisticItem,
          returnUrl: optimisticReturnUrl,
        });
      });

      try {
        const formData = createBookingFormData(item);
        await storeBookingIntent(formData);

        await queryClient.cancelQueries({
          queryKey: BOOKING_CART_QUERY_KEY,
        });

        queryClient.setQueryData(BOOKING_CART_QUERY_KEY, {
          cartItem: optimisticItem,
          returnUrl: optimisticReturnUrl,
        });

        void queryClient
          .invalidateQueries({
            queryKey: BOOKING_CART_QUERY_KEY,
          })
          .catch((error: unknown) => {
            console.error("Failed to refresh booking cart:", error);
          });
      } catch (error) {
        startTransition(() => {
          setOptimisticCartData({ cartItem, returnUrl });
        });
        console.error("Failed to store booking intent:", error);
        throw error;
      }
    },
    [cartItem, returnUrl, queryClient, setOptimisticCartData],
  );

  const clearBookingCart = useCallback(async () => {
    startTransition(() => {
      setOptimisticCartData({ cartItem: null, returnUrl: null });
    });

    try {
      await clearBookingIntent();

      queryClient.removeQueries({
        queryKey: BOOKING_CART_QUERY_KEY,
      });

      queryClient.setQueryData(BOOKING_CART_QUERY_KEY, {
        cartItem: null,
        returnUrl: null,
      });
    } catch (error) {
      startTransition(() => {
        setOptimisticCartData({ cartItem, returnUrl });
      });
      console.error("Failed to clear booking intent:", error);
      throw error;
    }
  }, [cartItem, returnUrl, queryClient, setOptimisticCartData]);

  const updateBookingStep = useCallback(
    async (step: number) => {
      if (!optimisticCartData.cartItem) return;

      const optimisticItem: BookingCartItem = {
        ...optimisticCartData.cartItem,
        currentStep: step,
      };

      startTransition(() => {
        setOptimisticCartData({
          cartItem: optimisticItem,
          returnUrl: optimisticCartData.returnUrl,
        });
      });

      try {
        await updateBookingIntentStep(step);

        await queryClient.invalidateQueries({
          queryKey: BOOKING_CART_QUERY_KEY,
        });
      } catch (error) {
        startTransition(() => {
          setOptimisticCartData({ cartItem, returnUrl });
        });
        console.error("Failed to update booking step:", error);
        throw error;
      }
    },
    [
      cartItem,
      returnUrl,
      optimisticCartData,
      queryClient,
      setOptimisticCartData,
    ],
  );

  return {
    cartItem: optimisticCartData.cartItem,
    returnUrl: optimisticCartData.returnUrl,
    isPending,
    isLoading,
    addToBookingCart,
    clearBookingCart,
    updateBookingStep,
  };
}

/**
 * Simplified hook for components that only need to read cart state
 */
export function useBookingCartState() {
  const { data: cartData, isLoading } = useQuery({
    queryKey: BOOKING_CART_QUERY_KEY,
    queryFn: getBookingCartData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });

  return {
    cartItem: cartData?.cartItem || null,
    hasBooking: Boolean(cartData?.cartItem),
    isLoading,
  };
}
